// Push notifications (Firebase Cloud Messaging).
//
// The display half already existed: public/firebase-messaging-sw.js renders
// background messages and defines the types new_job, job_scheduled,
// invoice_sent, invoice_paid and payment_overdue. The send half lives in
// communications/index.js. This is the piece in between - permission, token
// registration, and foreground messages.
//
// Nothing here runs at startup unless the user has already granted permission,
// and the messaging SDK is only imported at that point.

import { loadMessaging } from './vendor.js';

// Web Push certificate from Firebase console -> Project settings -> Cloud
// Messaging -> Web configuration. This is a PUBLIC key; it is safe in source.
// Carried over from the pre-Phase-0 implementation and NOT yet re-verified
// against the current project - if getToken() rejects with an applicationServerKey
// error, this is the first thing to check.
const VAPID_KEY =
  'BKYqjA3EvH38K3Kr3yyVrUVMpkZHk7vvxdkXgTgSEXKEvoa9QEl__7EYQGsP8vMEbnCekanmPWGQ_u23jX2j2vs';

const SW_URL = '/firebase-messaging-sw.js';

export function notificationsSupported() {
  return (
    typeof window !== 'undefined' &&
    'Notification' in window &&
    'serviceWorker' in navigator &&
    window.isSecureContext
  );
}

export function permissionState() {
  return notificationsSupported() ? Notification.permission : 'unsupported';
}

async function registerServiceWorker() {
  // Registered explicitly rather than left to the SDK's auto-detection, so a
  // failure here surfaces instead of turning into a silent getToken() error.
  const registration = await navigator.serviceWorker.register(SW_URL, {
    scope: '/',
  });
  await navigator.serviceWorker.ready;
  return registration;
}

async function saveToken(token) {
  const user = firebase.auth().currentUser;
  if (!user) throw new Error('Must be signed in to enable notifications.');

  const record = {
    userId: user.uid,
    userEmail: user.email || '',
    userAgent: navigator.userAgent,
    timestamp: firebase.database.ServerValue.TIMESTAMP,
  };

  // Two locations, matching the shape the security rules already describe:
  // users/{uid}/fcmToken is the user's current token, fcmTokens/{token} is the
  // flat index the send path and admin tooling read.
  await Promise.all([
    firebase.database().ref(`users/${user.uid}/fcmToken`).set(token),
    firebase.database().ref(`users/${user.uid}/fcmTokenData`).set(record),
    firebase.database().ref(`fcmTokens/${token.replace(/[.#$[\]/]/g, '_')}`).set(record),
  ]);
}

/** Ask for permission, register the worker, and store the token. */
export async function enableNotifications() {
  if (!notificationsSupported()) {
    return { ok: false, reason: 'unsupported' };
  }

  const permission = await Notification.requestPermission();
  if (permission !== 'granted') {
    return { ok: false, reason: permission }; // 'denied' or 'default'
  }

  try {
    const registration = await registerServiceWorker();
    const messaging = await loadMessaging();
    const token = await messaging.getToken({
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (!token) return { ok: false, reason: 'no-token' };

    await saveToken(token);
    listenForForegroundMessages(messaging);
    return { ok: true, token };
  } catch (error) {
    console.error('Enabling notifications failed:', error);
    return { ok: false, reason: error.message };
  }
}

/** Drop the token so this device stops receiving pushes. */
export async function disableNotifications() {
  try {
    const messaging = await loadMessaging();
    const user = firebase.auth().currentUser;
    const token = await messaging.getToken({ vapidKey: VAPID_KEY }).catch(() => null);

    await messaging.deleteToken();
    if (user) {
      await Promise.all([
        firebase.database().ref(`users/${user.uid}/fcmToken`).remove(),
        firebase.database().ref(`users/${user.uid}/fcmTokenData`).remove(),
        token
          ? firebase
              .database()
              .ref(`fcmTokens/${token.replace(/[.#$[\]/]/g, '_')}`)
              .remove()
          : Promise.resolve(),
      ]);
    }
    return { ok: true };
  } catch (error) {
    console.error('Disabling notifications failed:', error);
    return { ok: false, reason: error.message };
  }
}

let foregroundBound = false;

/**
 * Messages that arrive while the app is focused do NOT reach the service
 * worker, so the browser shows nothing unless we do it here.
 */
function listenForForegroundMessages(messaging) {
  if (foregroundBound) return;
  foregroundBound = true;

  messaging.onMessage((payload) => {
    const { notification = {}, data = {} } = payload;
    window.dispatchEvent(
      new CustomEvent('fcm-message', { detail: { notification, data } })
    );

    if (Notification.permission !== 'granted') return;
    const shown = new Notification(notification.title || data.title || 'Soft Wash Pros', {
      body: notification.body || data.body || '',
      icon: '/icon.png',
      tag: data.type || 'general',
      data,
    });
    shown.onclick = () => {
      window.focus();
      if (data.type === 'new_job') window.location.hash = '#/schedule';
      shown.close();
    };
  });
}

/**
 * Called once at startup. Only does anything for a user who already granted
 * permission, so a first-time visitor is never prompted and never pays for the
 * messaging SDK.
 */
export async function initNotifications() {
  if (!notificationsSupported() || Notification.permission !== 'granted') return;
  try {
    const registration = await registerServiceWorker();
    const messaging = await loadMessaging();
    const token = await messaging.getToken({
      vapidKey: VAPID_KEY,
      serviceWorkerRegistration: registration,
    });
    if (token) await saveToken(token); // refresh: tokens rotate
    listenForForegroundMessages(messaging);
  } catch (error) {
    console.error('Notification init failed:', error);
  }
}

/**
 * Diagnostic. From the browser console, signed in:
 *   await window.testNotification()
 * Returns what FCM reported, so a failure is visible rather than silent.
 */
export async function testNotification() {
  const state = {
    supported: notificationsSupported(),
    permission: permissionState(),
    serviceWorker: null,
    server: null,
  };
  try {
    const regs = await navigator.serviceWorker.getRegistrations();
    const fcm = regs.find((r) => (r.active && r.active.scriptURL || '').includes('firebase-messaging-sw'));
    state.serviceWorker = fcm
      ? { scope: fcm.scope, state: fcm.active && fcm.active.state }
      : 'NOT REGISTERED';
  } catch (e) {
    state.serviceWorker = 'error: ' + e.message;
  }
  try {
    const call = firebase.functions().httpsCallable('sendTestNotification');
    state.server = (await call()).data;
  } catch (e) {
    state.server = 'error: ' + e.message;
  }
  console.table ? console.table(state) : console.log(state);
  return state;
}
